import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

export default function ResetPage() {
    const [params] = useSearchParams();
    const signedToken = params.get("t") || "";
    const isSignedMode = !!signedToken;

    const [master, setMaster] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "إعادة تعيين حساب المدير";
        if (!isSignedMode) {
            api.get("/auth/reset/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false, reason: "error" }));
        } else {
            setStatus({ enabled: true, mode: "signed" });
        }
    }, [isSignedMode]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 12) return toast.error("كلمة المرور يجب أن تكون 12 حرفًا فأكثر");
        if (password !== confirm) return toast.error("تأكيد كلمة المرور لا يطابق");
        setSaving(true);
        try {
            if (isSignedMode) {
                const { data } = await api.post("/auth/reset/from-signed", {
                    token: signedToken,
                    new_username: username,
                    new_password: password,
                    confirm_new_password: confirm,
                });
                toast.success(`تم تعيين حساب المدير: @${data.username}`);
            } else {
                const { data } = await api.post("/auth/reset/confirm", {
                    master_token: master,
                    new_username: username,
                    new_password: password,
                    confirm_new_password: confirm,
                });
                toast.success(`تم تعيين حساب المدير: @${data.username}`);
            }
            navigate("/login");
        } catch (e) {
            toast.error(formatError(e.response?.data?.detail) || e.message);
        } finally { setSaving(false); }
    };

    const disabledMsg = (!isSignedMode && status && !status.enabled)
        ? (status.reason === "disabled" ? "خدمة إعادة التعيين غير مفعّلة. استخدم رابطًا موقّعًا آمنًا (ينتهي بـ ?t=...) أو أضف ADMIN_MASTER_RESET_TOKEN في البيئة الآمنة."
        : status.reason === "already_used" ? "تم استخدام رابط إعادة التعيين مسبقًا. لإعادة التفعيل، غيّر قيمة ADMIN_MASTER_RESET_TOKEN وأعد تشغيل الخدمة."
        : status.reason === "expired" ? "انتهت صلاحية نافذة إعادة التعيين. أعد تشغيل الخدمة لبدء نافذة جديدة."
        : "خدمة إعادة التعيين غير متوفّرة حاليًا.")
        : null;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 public-scope ui-surface relative">
            <div className="absolute top-4 end-4 z-10" dir="ltr"><ThemeToggle /></div>
            <div className="w-full max-w-md bg-white rounded-sm border border-gray-200 shadow-sm p-8">
                <div className="flex items-center justify-center gap-2 mb-4 text-brand-red">
                    <ShieldCheck className="w-8 h-8" />
                </div>
                <h1 className="font-display font-black text-2xl mb-1 text-brand-black text-center">إعادة تعيين حساب المدير</h1>
                <p className="text-sm text-gray-500 mb-6 text-center">
                    {isSignedMode ? "رابط موقّع آمن — أدخل بيانات الحساب الجديدة." : "أدخل رمز الاستعادة الرئيسي وبيانات الحساب الجديدة."}
                </p>

                {disabledMsg && (
                    <div className="mb-6 flex gap-3 rounded-sm border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="reset-disabled">
                        <AlertTriangle className="w-5 h-5 shrink-0" /> <span>{disabledMsg}</span>
                    </div>
                )}

                <form onSubmit={submit} className="space-y-4">
                    {!isSignedMode && (
                        <div>
                            <Label className="mb-1 block flex items-center gap-1"><KeyRound className="w-4 h-4 text-brand-red" /> رمز الاستعادة الرئيسي</Label>
                            <Input type="password" autoComplete="one-time-code" value={master} onChange={(e) => setMaster(e.target.value)} required={!isSignedMode} className="rounded-sm" data-testid="reset-master" />
                            <p className="text-xs text-gray-500 mt-1">القيمة التي عيّنتها في المتغيّر <code className="bg-gray-100 px-1 rounded">ADMIN_MASTER_RESET_TOKEN</code>.</p>
                        </div>
                    )}
                    <div>
                        <Label className="mb-1 block">اسم المستخدم الجديد</Label>
                        <Input type="text" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} required placeholder="admin" className="rounded-sm" data-testid="reset-username" />
                    </div>
                    <div>
                        <Label className="mb-1 block">كلمة المرور الجديدة</Label>
                        <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-sm" data-testid="reset-password" />
                        {password && password.length < 12 && (
                            <p className="text-xs text-brand-red mt-1">حروف مطلوبة: {12 - password.length} حرفًا إضافيًا (الحد الأدنى 12).</p>
                        )}
                    </div>
                    <div>
                        <Label className="mb-1 block">تأكيد كلمة المرور</Label>
                        <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="rounded-sm" data-testid="reset-confirm" />
                    </div>
                    <Button type="submit" disabled={saving || (!isSignedMode && status && !status.enabled)} className="w-full bg-brand-red hover:bg-brand-red-dark rounded-sm" data-testid="reset-submit">
                        {saving ? "جارٍ التعيين..." : "تعيين حساب جديد"}
                    </Button>
                </form>
                <Link to="/login" className="mt-6 block text-center text-sm text-gray-500 hover:text-brand-red">العودة إلى تسجيل الدخول</Link>
            </div>
        </div>
    );
}
